import React, { ReactNode } from 'react';

import { ChipProps, Icon } from '@column/column-ui-kit';

import { getTransferURL } from '~/lib/transfers/utils';
import { ACHTransferBatch, ACHTransferBatchRecord } from '~/typings/API';

export type RecordStatusMeta = { type: ChipProps['type']; label: string };

export const RECORD_STATUS_META: Record<string, RecordStatusMeta> = {
  pending: { type: 'default', label: 'Pending' },
  valid: { type: 'info', label: 'Valid' },
  invalid: { type: 'danger', label: 'Invalid' },
  succeeded: { type: 'success', label: 'Succeeded' },
  failed: { type: 'danger', label: 'Failed' },
};

export type BatchStatusMeta = { type: ChipProps['type']; label: string; icon: ReactNode };

export const BATCH_STATUS_META: Record<string, BatchStatusMeta> = {
  pending: { type: 'default', label: 'Pending', icon: <Icon.CircleBlank /> },
  validating: { type: 'info', label: 'Validating', icon: <Icon.CirclePendingOne /> },
  validated: { type: 'info', label: 'Validated', icon: <Icon.CirclePendingTwo /> },
  processing: { type: 'info', label: 'Processing', icon: <Icon.CirclePendingThree /> },
  completed: { type: 'success', label: 'Completed', icon: <Icon.CircleCheck /> },
  failed: { type: 'danger', label: 'Failed', icon: <Icon.CircleCross /> },
  expired: { type: 'danger', label: 'Expired', icon: <Icon.CircleCross /> },
  canceled: { type: 'danger', label: 'Canceled', icon: <Icon.CircleCross /> },
};

const DEFAULT_BATCH_STATUS: BatchStatusMeta = {
  type: 'default',
  label: '',
  icon: <Icon.CircleBlank />,
};

export const transferUrlFor = (record: ACHTransferBatchRecord): string | undefined =>
  record.achTransferId
    ? getTransferURL(record.achTransferId, record.type === 'debit' ? 'ach_debit' : 'ach_credit')
    : undefined;

const isAllInvalid = (batch: ACHTransferBatch): boolean =>
  batch.status === 'validated' && batch.totalCount > 0 && batch.validationFailedCount === batch.totalCount;

const isAllFailed = (batch: ACHTransferBatch): boolean =>
  batch.status === 'completed' && batch.failedCount > 0 && batch.succeededCount === 0;

export const getBatchStatusDisplay = (batch: ACHTransferBatch): BatchStatusMeta => {
  if (isAllInvalid(batch)) return { type: 'danger', label: 'All Invalid', icon: <Icon.CircleCross /> };
  if (isAllFailed(batch)) return { type: 'danger', label: 'All Failed', icon: <Icon.CircleCross /> };
  return BATCH_STATUS_META[batch.status] ?? { ...DEFAULT_BATCH_STATUS, label: batch.status };
};

export const hasCreatableTransfers = (batch: ACHTransferBatch): boolean =>
  batch.status === 'validated' && batch.totalCount - batch.validationFailedCount > 0;

const isPostCreation = (batch: ACHTransferBatch): boolean => !!batch.completedAt || batch.status === 'failed';

export const buildBreakdownText = (batch: ACHTransferBatch): string => {
  let parts: { label: string; count: number }[] = [];
  if (isPostCreation(batch)) {
    parts = [
      { label: 'Created', count: batch.succeededCount },
      { label: 'Failed', count: batch.failedCount },
      { label: 'Invalid', count: batch.validationFailedCount },
    ];
  } else if (batch.validatedAt) {
    parts = [
      { label: 'Validated', count: batch.totalCount - batch.validationFailedCount },
      { label: 'Invalid', count: batch.validationFailedCount },
    ];
  }
  return parts
    .filter((p) => p.count > 0)
    .map((p) => `${p.count} ${p.label}`)
    .join(' · ');
};

export type BatchAmounts = {
  phase: 'validated' | 'created';
  credit: number;
  debit: number;
};

export const pickBatchAmounts = (batch: ACHTransferBatch): BatchAmounts | null => {
  if (batch.status === 'canceled' || batch.status === 'expired') return null;
  if (isAllInvalid(batch) || isAllFailed(batch)) return null;
  if (isPostCreation(batch)) {
    return { phase: 'created', credit: batch.totalCreditAmount, debit: batch.totalDebitAmount };
  }
  if (batch.validatedAt) {
    return {
      phase: 'validated',
      credit: batch.validatedTotalCreditAmount,
      debit: batch.validatedTotalDebitAmount,
    };
  }
  return null;
};
